import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ChevronLeft,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronRight,
  Eye,
  Loader2,
  Lock,
  ScrollText,
  Search,
  X,
} from "lucide-react";
import { Main } from "@/components/Main";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/hooks/auth";
import { usePermissions } from "@/hooks/usePermissions";
import { PERMISSIONS } from "@/constants/permissions";
import { TypeUser } from "@/types/userType";
import {
  ActionLogFilters,
  ActionLogModule,
  ActionLogSortColumn,
  getActionLogOptions,
  getActionLogs,
} from "@/Api/actionLogs";
import { ActionLogDetailModal } from "./components/ActionLogDetailModal";
import {
  ActionLogOptions,
  ActionLogRow,
  MODULE_LABELS,
  Paginated,
  actionBadgeClass,
  entityLink,
  formatDateTime,
} from "./types";

/** Valor usado pelos selects para "todos" (o Radix não aceita value vazio). */
const ALL = "all";

const toInputDate = (date: Date) => {
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
};

const defaultFilters = (): ActionLogFilters => {
  const today = new Date();
  const start = new Date();
  start.setDate(today.getDate() - 6);

  return {
    log_action_id: "",
    module: "",
    user_id: "",
    seller_id: "",
    unity_id: "",
    search: "",
    date_start: toInputDate(start),
    date_end: toInputDate(today),
    per_page: 20,
    order_by: "created_at",
    order_direction: "desc",
  };
};

/**
 * Gerenciamento de logs de ações (LOT-19).
 *
 * Mostra quem executou cada ação de concursos e vendas. A verificação de
 * acesso aqui é apenas de interface: o backend protege os endpoints com o
 * middleware "panel.permission:view-action-logs" e limita ao tenant do usuário.
 */
export const PageList = () => {
  const { user } = useAuth();
  const { user: currentUser, hasPermission } = usePermissions();

  const isSuperAdmin = user?.user_type_id === TypeUser.admin;
  const isTenantUser = user?.user_type_id === TypeUser.client;
  const isTenantAdmin =
    isTenantUser && hasPermission(PERMISSIONS.VIEW_ACTION_LOGS);
  const isCheckingPermissions = !isSuperAdmin && isTenantUser && !currentUser;
  const canAccess = isSuperAdmin || isTenantAdmin;

  const [options, setOptions] = useState<ActionLogOptions | null>(null);
  const [filters, setFilters] = useState<ActionLogFilters>(defaultFilters);
  const [appliedFilters, setAppliedFilters] =
    useState<ActionLogFilters>(defaultFilters);
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<Paginated<ActionLogRow> | null>(null);
  const [loading, setLoading] = useState(false);
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);

  const closeDetail = useCallback(() => setSelectedUuid(null), []);

  useEffect(() => {
    if (!canAccess) return;

    (async () => {
      const response = await getActionLogOptions();

      if (response.ok) {
        setOptions(response.data?.response ?? null);
      }
    })();
  }, [canAccess]);

  useEffect(() => {
    if (!canAccess) return;

    let active = true;

    (async () => {
      setLoading(true);
      const response = await getActionLogs(page, appliedFilters);

      if (!active) return;

      setLoading(false);

      if (!response.ok) {
        toast.error(
          response.data?.message ?? "Não foi possível carregar os logs."
        );
        return;
      }

      setResult(response.data?.response ?? null);
    })();

    return () => {
      active = false;
    };
  }, [canAccess, page, appliedFilters]);

  /** Ações agrupadas por módulo, respeitando o módulo filtrado. */
  const groupedActions = useMemo(() => {
    const actions = (options?.actions ?? []).filter(
      (action) => !filters.module || action.module === filters.module
    );

    return (Object.keys(MODULE_LABELS) as ActionLogModule[])
      .map((module) => ({
        module,
        actions: actions.filter((action) => action.module === module),
      }))
      .filter((group) => group.actions.length > 0);
  }, [options, filters.module]);

  const updateFilter = (key: keyof ActionLogFilters, value: string) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: value === ALL ? "" : value };

      // Trocar o módulo limpa uma ação que não pertença a ele.
      if (key === "module" && next.log_action_id) {
        const action = options?.actions.find(
          (item) => String(item.id) === next.log_action_id
        );

        if (action && next.module && action.module !== next.module) {
          next.log_action_id = "";
        }
      }

      return next;
    });
  };

  const applyFilters = () => {
    if (
      filters.date_start &&
      filters.date_end &&
      filters.date_end < filters.date_start
    ) {
      toast.error("A data final deve ser igual ou posterior à data inicial.");
      return;
    }

    setPage(1);
    setAppliedFilters({ ...filters });
  };

  /** Alterna asc/desc na coluna; coluna nova começa em ascendente. */
  const toggleSort = (column: ActionLogSortColumn) => {
    setPage(1);
    setAppliedFilters((prev) => ({
      ...prev,
      order_by: column,
      order_direction:
        prev.order_by === column && prev.order_direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const renderSortHead = (
    column: ActionLogSortColumn,
    label: string,
    className?: string
  ) => {
    const active = appliedFilters.order_by === column;
    const Icon = !active
      ? ArrowUpDown
      : appliedFilters.order_direction === "asc"
      ? ArrowUp
      : ArrowDown;

    return (
      <TableHead
        className={className}
        aria-sort={
          active
            ? appliedFilters.order_direction === "asc"
              ? "ascending"
              : "descending"
            : "none"
        }
      >
        <button
          type="button"
          onClick={() => toggleSort(column)}
          className="inline-flex items-center gap-1 hover:text-foreground"
        >
          {label}
          <Icon
            className={`h-3.5 w-3.5 ${active ? "" : "text-muted-foreground/60"}`}
          />
        </button>
      </TableHead>
    );
  };

  const clearFilters = () => {
    const initial = defaultFilters();
    setFilters(initial);
    setPage(1);
    setAppliedFilters(initial);
  };

  if (isCheckingPermissions) {
    return (
      <Main>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </Main>
    );
  }

  if (!canAccess) {
    return (
      <Main>
        <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
          <Lock className="h-10 w-10 text-muted-foreground" />
          <h2 className="text-lg font-semibold">Acesso restrito</h2>
          <p className="max-w-md text-sm text-muted-foreground">
            Os logs de ações estão disponíveis para o Super Admin e para
            administradores com a permissão correspondente.
          </p>
        </div>
      </Main>
    );
  }

  const rows = result?.data ?? [];

  return (
    <Main>
      <PageHeader
        icon={ScrollText}
        title="Logs de Ações"
        description="Quem executou cada ação em concursos e vendas"
      />

      <Card className="mb-4">
        <CardContent className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 lg:grid-cols-6">
          <div>
            <Label className="mb-1 block">Módulo</Label>
            <Select
              value={filters.module || ALL}
              onValueChange={(value) => updateFilter("module", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Todos</SelectItem>
                {(options?.modules ?? []).map((module) => (
                  <SelectItem key={module.value} value={module.value}>
                    {module.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="lg:col-span-2">
            <Label className="mb-1 block">Ação</Label>
            <Select
              value={filters.log_action_id || ALL}
              onValueChange={(value) => updateFilter("log_action_id", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Todas" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Todas</SelectItem>
                {groupedActions.map((group) => (
                  <SelectGroup key={group.module}>
                    <SelectLabel>{MODULE_LABELS[group.module]}</SelectLabel>
                    {group.actions.map((action) => (
                      <SelectItem key={action.id} value={String(action.id)}>
                        {action.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="mb-1 block">Usuário</Label>
            <Select
              value={filters.user_id || ALL}
              onValueChange={(value) => updateFilter("user_id", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Todos</SelectItem>
                {(options?.users ?? []).map((item) => (
                  <SelectItem key={item.id} value={String(item.id)}>
                    {item.name ?? `Usuário #${item.id}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="action-log-date-start" className="mb-1 block">
              Data inicial
            </Label>
            <Input
              id="action-log-date-start"
              type="date"
              value={filters.date_start ?? ""}
              onChange={(event) =>
                updateFilter("date_start", event.target.value)
              }
            />
          </div>

          <div>
            <Label htmlFor="action-log-date-end" className="mb-1 block">
              Data final
            </Label>
            <Input
              id="action-log-date-end"
              type="date"
              value={filters.date_end ?? ""}
              onChange={(event) => updateFilter("date_end", event.target.value)}
            />
          </div>

          <div>
            <Label className="mb-1 block">Vendedor</Label>
            <Select
              value={filters.seller_id || ALL}
              onValueChange={(value) => updateFilter("seller_id", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Todos</SelectItem>
                {(options?.sellers ?? []).map((item) => (
                  <SelectItem key={item.id} value={String(item.id)}>
                    {item.name ?? `Vendedor #${item.id}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="mb-1 block">Unidade</Label>
            <Select
              value={filters.unity_id || ALL}
              onValueChange={(value) => updateFilter("unity_id", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Todas" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Todas</SelectItem>
                {(options?.unities ?? []).map((item) => (
                  <SelectItem key={item.id} value={String(item.id)}>
                    {item.name ?? `Unidade #${item.id}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="sm:col-span-2 lg:col-span-2">
            <Label htmlFor="action-log-search" className="mb-1 block">
              Registro
            </Label>
            <Input
              id="action-log-search"
              value={filters.search ?? ""}
              placeholder="Nome do concurso, nº da venda ou usuário"
              onChange={(event) => updateFilter("search", event.target.value)}
              onKeyDown={(event) => event.key === "Enter" && applyFilters()}
            />
          </div>

          <div className="flex items-end gap-2 sm:col-span-2">
            <Button
              onClick={applyFilters}
              disabled={loading}
              className="flex-1"
            >
              {loading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Search className="mr-2 h-4 w-4" />
              )}
              Filtrar
            </Button>
            <Button variant="outline" onClick={clearFilters} disabled={loading}>
              <X className="mr-2 h-4 w-4" />
              Limpar
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                {renderSortHead("created_at", "Data e hora", "w-44")}
                {renderSortHead("user_name", "Usuário")}
                {renderSortHead("action", "Ação")}
                {renderSortHead("entity_label", "Registro")}
                {renderSortHead(
                  "description",
                  "Descrição",
                  "hidden lg:table-cell"
                )}
                {renderSortHead("ip_address", "IP", "hidden md:table-cell")}
                <TableHead className="w-16" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="py-10 text-center">
                    <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
                  </TableCell>
                </TableRow>
              )}

              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="py-10 text-center text-muted-foreground"
                  >
                    Nenhum log encontrado para os filtros informados.
                  </TableCell>
                </TableRow>
              )}

              {rows.map((row) => {
                const link = entityLink(row);

                return (
                  <TableRow
                    key={row.uuid}
                    className={loading ? "opacity-60" : undefined}
                  >
                    <TableCell className="whitespace-nowrap text-sm">
                      {formatDateTime(row.created_at)}
                    </TableCell>
                    <TableCell>{row.user_name ?? "—"}</TableCell>
                    <TableCell>
                      <div className="flex flex-col items-start gap-1">
                        <Badge className={actionBadgeClass(row.log_action.key)}>
                          {row.log_action.label}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {MODULE_LABELS[row.log_action.module]}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {link ? (
                        <Link
                          to={link}
                          className="text-primary underline-offset-4 hover:underline"
                        >
                          {row.entity_label ?? "—"}
                        </Link>
                      ) : (
                        row.entity_label ?? "—"
                      )}
                      {(row.seller || row.unity) && (
                        <div className="text-xs text-muted-foreground">
                          {[row.seller?.user?.name, row.unity?.name]
                            .filter(Boolean)
                            .join(" · ")}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="hidden max-w-md text-sm text-muted-foreground lg:table-cell">
                      {row.description ?? "—"}
                    </TableCell>
                    <TableCell className="hidden text-sm md:table-cell">
                      {row.ip_address ?? "—"}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-blue-600 hover:bg-blue-50 hover:text-blue-800 dark:text-blue-400 dark:hover:bg-gray-800 dark:hover:text-blue-300"
                        title="Ver detalhes"
                        aria-label="Ver detalhes"
                        onClick={() => setSelectedUuid(row.uuid)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {result && result.total > 0 && (
        <div className="mt-3 flex items-center justify-between text-sm text-muted-foreground">
          <span>
            {result.total} registro{result.total === 1 ? "" : "s"} · página{" "}
            {result.current_page} de {result.last_page}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={loading || result.current_page <= 1}
              onClick={() => setPage((current) => current - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={loading || result.current_page >= result.last_page}
              onClick={() => setPage((current) => current + 1)}
            >
              Próxima
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <ActionLogDetailModal uuid={selectedUuid} onClose={closeDetail} />
    </Main>
  );
};

export default PageList;
